import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { RepairComment } from '../../api/api-generated';
import {
  useCreateRepairCommentMutation,
  useFindAllRepairCommentsQuery,
  useRemoveRepairCommentMutation,
  useUpdateRepairCommentMutation,
} from '../../api/api-hooks';
import { truncateISOString } from '../../utils/dateTime';

interface Props {
  clickedRma: string | number;
  commentsDialogOpen: boolean;
  setCommentsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface NewComment {
  id: undefined;
  commentBody: string;
  rmaId: number;
  dateCreated: string;
  newId: number;
}

export default function LptRmaCommentsDialog({
  clickedRma,
  commentsDialogOpen,
  setCommentsDialogOpen,
}: Props) {
  const { data: comments } = useFindAllRepairCommentsQuery({});
  const [commentUpdater] = useUpdateRepairCommentMutation();
  const [commentAdder] = useCreateRepairCommentMutation();
  const [commentDeleter] = useRemoveRepairCommentMutation();
  const [formState, setFormState] =
    useState<Array<RepairComment | NewComment>>();
  const [idsToDelete, setIdsToDelete] = useState<number[]>([]);
  const newIdRef = useRef<number>(0);
  useEffect(() => {
    if (comments === undefined) return;
    const rmaComments = comments.filter(
      (comment) => comment.rmaId === Number(clickedRma)
    );
    setFormState(rmaComments);
  }, [comments, clickedRma, commentsDialogOpen]);
  if (comments === undefined || formState === undefined) return null;
  const handleChange = (event: any) => {
    const { name, value } = event.target;
    const newFormState = formState.map((item: RepairComment | NewComment) => {
      if (
        item.id === Number(name) ||
        ('newId' in item && item.newId === Number(name))
      )
        return { ...item, commentBody: value };
      return item;
    });
    setFormState(newFormState);
  };
  const handleDelete = (comment: RepairComment | NewComment) => {
    const { id } = comment;
    const newId = 'newId' in comment ? comment.newId : undefined;
    setFormState((priorState) =>
      priorState?.filter((comment) => {
        if (comment.id && comment.id === id) {
          setIdsToDelete((priorState) => [...priorState, comment.id]);
          return false;
        }
        if ('newId' in comment) {
          if (comment.newId === newId) return false;
        }
        return true;
      })
    );
  };
  const handleClose = () => {
    setFormState([]);
    setCommentsDialogOpen(false);
  };
  const handleSave = async () => {
    setCommentsDialogOpen(false);
    for (const comment of formState) {
      if ('newId' in comment) {
        if (comment.commentBody)
          await commentAdder({ createRepairCommentDto: comment });
      } else {
        await commentUpdater({
          id: comment.id,
          updateRepairCommentDto: comment,
        });
      }
    }
    for (const id of idsToDelete) {
      commentDeleter({ id });
    }
    setFormState([]);
  };
  const handleAdd = async () => {
    setFormState((priorState) => {
      if (!priorState)
        return [
          {
            id: undefined,
            commentBody: '',
            rmaId: Number(clickedRma),
            dateCreated: new Date().toISOString(),
            newId: newIdRef.current++,
          },
        ];
      else
        return [
          ...priorState,
          {
            id: undefined,
            commentBody: '',
            rmaId: Number(clickedRma),
            dateCreated: new Date().toISOString(),
            newId: newIdRef.current++,
          },
        ];
    });
  };
  return (
    <Dialog
      fullWidth
      open={commentsDialogOpen}
      aria-labelledby='form-dialog-title'
    >
      <DialogTitle id='form-dialog-title'>Repair Comments</DialogTitle>
      <DialogContent>
        <Grid container direction='column' spacing={2}>
          {formState.length === 0 && (
            <Grid item>
              <Typography>No items found.</Typography>
            </Grid>
          )}
          {formState.length > 0 &&
            formState.map((item: RepairComment | NewComment) => (
              <Grid key={item.id} item>
                <TextField
                  fullWidth
                  name={'newId' in item ? '' + item.newId : '' + item.id}
                  value={item.commentBody}
                  onChange={handleChange}
                />
                <Grid
                  container
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <Grid item>
                    <Typography variant='caption'>
                      Created {truncateISOString(item.dateCreated)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Button
                      onClick={() => {
                        handleDelete(item);
                      }}
                    >
                      Delete
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            ))}
          <Grid item>
            <Button variant='contained' color='primary' onClick={handleAdd}>
              Add Item
            </Button>
          </Grid>

          <Grid item>
            <Typography color='error'>
              You must click Save to save your changes.
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} color='primary'>
          Save
        </Button>
        <Button onClick={handleClose} color='error'>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
